import { useCallback, useEffect, useRef, useState } from 'react';
import {
    SNAPSHOT_VIEWPORT,
    SNAPSHOT_WINDOWS,
    SNAPSHOT_TITLE_BAR_HEIGHT,
    SNAPSHOT_MIN_GRAB,
} from './desktopSnapshot';

const LS_KEY = 'hsh.dashboard.desktop.windows';
const PANEL_NAMES = [
    'aiAdvisor', 'yourHotel', 'competitors',
    'action', 'lodging', 'research', 'ghost',
    'orbitalRiver', 'activity', 'insights', 'scores'];

// Measure the canvas. Returns null if the ref isn't live yet.
// Returns floored integer dimensions when it is. This is the
// single source of truth for "what is the canvas size right now"
// across the hook — every clamp path routes through here so a
// viewport coord never sneaks in as a canvas proxy.
function measureCanvas(canvasRef) {
    if (!canvasRef || !canvasRef.current) return null;
    const el = canvasRef.current;
    if (typeof el.getBoundingClientRect !== 'function') return null;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    return {
        width: Math.floor(rect.width),
        height: Math.floor(rect.height),
    };
}

// Scale a single window from snapshot coordinates to a target
// viewport, proportionally on each axis. Returns a fresh window
// object — never mutates input. z passes through untouched.
export function scaleWindow(win, snapW, snapH, targetW, targetH) {
    const sx = targetW / snapW;
    const sy = targetH / snapH;
    return {
        x: Math.round(win.x * sx),
        y: Math.round(win.y * sy),
        width: Math.round(win.width * sx),
        height: Math.round(win.height * sy),
        z: win.z,
    };
}

// Apply scaling to every panel. Returns a fresh windows object.
export function applySnapshot(snapshot, snapViewport, targetW, targetH) {
    const next = {};
    for (const name of PANEL_NAMES) {
        const win = snapshot[name];
        if (!win) continue;
        next[name] = scaleWindow(
            win, snapViewport.width, snapViewport.height,
            targetW, targetH);
    }
    return next;
}

// Per-window clamp. Body may overflow the right or bottom edge
// of the canvas — width/height are preserved. The title bar must
// remain reachable: vertically it must intersect [0, canvasH -
// titleBarH], horizontally at least MIN_GRAB px must overlap the
// canvas. Returns a fresh window object.
export function clampWindow(win, canvasW, canvasH) {
    const titleBarH = SNAPSHOT_TITLE_BAR_HEIGHT;
    const minGrab = SNAPSHOT_MIN_GRAB;
    let { x, y, width, height, z } = win;

    const maxY = Math.max(0, canvasH - titleBarH);
    if (y < 0) y = 0;
    if (y > maxY) y = maxY;

    const grab = Math.min(minGrab, width);
    if (x + width < grab) x = grab - width;
    if (x > canvasW - grab) x = canvasW - grab;

    return { x, y, width, height, z };
}

// Clamp every panel in a windows object. Returns a fresh object.
export function clampWindows(windows, canvasW, canvasH) {
    const next = {};
    for (const name of PANEL_NAMES) {
        const win = windows[name];
        if (!win) continue;
        next[name] = clampWindow(win, canvasW, canvasH);
    }
    return next;
}

// Compute defaults from snapshot. Prefers canvas measurement when
// a live ref is available, otherwise falls back to window.inner*.
// Output is always: scale snapshot then clamp per window.
export function computeDefaults(canvasEl) {
    let targetW;
    let targetH;
    if (canvasEl
        && typeof canvasEl.getBoundingClientRect === 'function') {
        const rect = canvasEl.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
            targetW = Math.floor(rect.width);
            targetH = Math.floor(rect.height);
        }
    }
    if (typeof targetW !== 'number'
        || typeof targetH !== 'number') {
        targetW = (typeof window !== 'undefined'
            && typeof window.innerWidth === 'number')
            ? window.innerWidth : SNAPSHOT_VIEWPORT.width;
        targetH = (typeof window !== 'undefined'
            && typeof window.innerHeight === 'number')
            ? window.innerHeight : SNAPSHOT_VIEWPORT.height;
    }
    const scaled = applySnapshot(
        SNAPSHOT_WINDOWS, SNAPSHOT_VIEWPORT, targetW, targetH);
    return clampWindows(scaled, targetW, targetH);
}

function readStored() {
    try {
        const raw = window.localStorage.getItem(LS_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // Reject if any required panel is missing — schema migration
        // is just "redefault" for now. Stored layouts predating the
        // lodging panel land here on first read after upgrade.
        for (const name of PANEL_NAMES) {
            if (!parsed[name]) return null;
        }
        return parsed;
    } catch (_err) {
        return null;
    }
}

function writeStored(windows) {
    try {
        window.localStorage.setItem(LS_KEY, JSON.stringify(windows));
    } catch (_err) {
        // ignored — state still drives UI for this session.
    }
}

export function useFloatingWindows(canvasRef) {
    const [windows, setWindows] = useState(() => {
        const stored = readStored();
        const targetW = (typeof window !== 'undefined'
            && typeof window.innerWidth === 'number')
            ? window.innerWidth : SNAPSHOT_VIEWPORT.width;
        const targetH = (typeof window !== 'undefined'
            && typeof window.innerHeight === 'number')
            ? window.innerHeight : SNAPSHOT_VIEWPORT.height;
        if (stored) {
            // Per-window clamp the stored layout instead of
            // throwing it away wholesale. Preserves the user's
            // last-known arrangement; only nudges off-screen
            // windows back so their title bars are grabbable.
            return clampWindows(stored, targetW, targetH);
        }
        return computeDefaults(null);
    });

    // Track the highest z so bringToFront can increment past it.
    const maxZRef = useRef(
        Math.max(...Object.values(windows).map((w) => w.z || 0)));

    // Guards the post-mount initial canvas re-clamp. The useState
    // initializer can't read canvasRef.current (still null at that
    // point), so the first paint seeds against window.inner*. Once
    // the canvas resolves we re-clamp exactly once.
    const didInitialCanvasClampRef = useRef(false);

    const persist = useCallback((next) => {
        setWindows(next);
        writeStored(next);
    }, []);

    const updateWindow = useCallback((name, partial) => {
        setWindows((prev) => {
            if (!prev[name]) return prev;
            const merged = { ...prev[name], ...partial };
            const dims = measureCanvas(canvasRef);
            const clamped = dims
                ? clampWindow(merged, dims.width, dims.height)
                : merged;
            const next = { ...prev, [name]: clamped };
            writeStored(next);
            return next;
        });
    }, [canvasRef]);

    const bringToFront = useCallback((name) => {
        setWindows((prev) => {
            if (!prev[name]) return prev;
            // No-op if already on top.
            const currentZ = prev[name].z;
            if (currentZ === maxZRef.current
                && Object.values(prev)
                    .filter((w) => w !== prev[name])
                    .every((w) => w.z < currentZ)) {
                return prev;
            }
            const nextZ = maxZRef.current + 1;
            maxZRef.current = nextZ;
            const next = {
                ...prev,
                [name]: { ...prev[name], z: nextZ },
            };
            writeStored(next);
            return next;
        });
    }, []);

    const resetLayout = useCallback(() => {
        const canvasEl = (canvasRef && canvasRef.current)
            ? canvasRef.current : null;
        const next = computeDefaults(canvasEl);
        maxZRef.current = Math.max(
            ...Object.values(next).map((w) => w.z));
        persist(next);
    }, [persist, canvasRef]);

    const dumpLayout = useCallback(() => {
        const payload = {
            viewport: {
                width: (typeof window !== 'undefined'
                    && typeof window.innerWidth === 'number')
                    ? window.innerWidth : null,
                height: (typeof window !== 'undefined'
                    && typeof window.innerHeight === 'number')
                    ? window.innerHeight : null,
            },
            windows: windows,
        };
        const text = JSON.stringify(payload, null, 4);
        // eslint-disable-next-line no-console
        console.log('[FloatingWindows] Dump Layout\n' + text);
        if (typeof navigator !== 'undefined'
            && navigator.clipboard
            && typeof navigator.clipboard.writeText === 'function') {
            // Fire-and-forget. Failures still get the console log.
            navigator.clipboard.writeText(text).catch(() => {});
        }
        return payload;
    }, [windows]);

    // Persist the default on first mount so a reload keeps state.
    useEffect(() => {
        if (!readStored()) writeStored(windows);
    // eslint-disable-next-line
    }, []);

    // Initial canvas re-clamp. The useState initializer fell back
    // to window.inner* because canvasRef.current was null at that
    // moment. Once the canvas mounts and has a measurable rect,
    // clamp against the real canvas — viewport is bigger than the
    // canvas (top bar + preset picker + dashboard chrome eat ~120
    // px vertically), so windows seeded against viewport can land
    // below the canvas and become ungrabbable. No dep array on
    // purpose: the effect runs every render until the canvas is
    // live, then the ref guard flips and every subsequent run is
    // a one-line no-op.
    // eslint-disable-next-line
    useEffect(() => {
        if (didInitialCanvasClampRef.current) return;
        const dims = measureCanvas(canvasRef);
        if (!dims) return;
        didInitialCanvasClampRef.current = true;
        setWindows((prev) => {
            const next = clampWindows(prev, dims.width, dims.height);
            writeStored(next);
            return next;
        });
    });

    // Re-clamp on window resize. The user can resize the browser,
    // the OS can change DPI, the dashboard chrome can change
    // height as game state evolves — any of those can shrink the
    // canvas under existing window positions and strand title
    // bars below the visible area.
    useEffect(() => {
        function handleResize() {
            const dims = measureCanvas(canvasRef);
            if (!dims) return;
            setWindows((prev) => {
                const next = clampWindows(
                    prev, dims.width, dims.height);
                writeStored(next);
                return next;
            });
        }
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [canvasRef]);

    return {
        windows, updateWindow, bringToFront,
        resetLayout, dumpLayout,
    };
}
